import { useCallback, useEffect, useRef, useState } from 'react'

type DailyCallLike = {
  leave: () => Promise<unknown>
}

function throttle<T extends (...args: unknown[]) => void>(fn: T, ms: number): T {
  let last = 0
  return ((...args: unknown[]) => {
    const now = Date.now()
    if (now - last >= ms) {
      last = now
      fn(...args)
    }
  }) as T
}

export type IdleDailyDisconnectOptions = {
  /** Khi false, dừng mọi timer và đóng dialog. */
  enabled: boolean
  getCallFrame: () => DailyCallLike | null | undefined
  warnAfterMs: number
  graceAfterWarnMs: number
}

/**
 * Cảnh báo rồi tự leave() Daily khi người dùng không tương tác với trang (treo máy mở tab).
 * Không áp dụng khi mở Daily ở tab khác (không có call frame trong trang).
 */
export function useIdleDailyDisconnect(options: IdleDailyDisconnectOptions) {
  const { enabled, getCallFrame, warnAfterMs, graceAfterWarnMs } = options
  const lastActivityRef = useRef(Date.now())
  const warnActiveRef = useRef(false)
  const warnStartedAtRef = useRef<number | null>(null)
  const [warnModalOpen, setWarnModalOpen] = useState(false)
  const [secondsUntilLeave, setSecondsUntilLeave] = useState(0)

  const acknowledge = useCallback(() => {
    lastActivityRef.current = Date.now()
    warnActiveRef.current = false
    warnStartedAtRef.current = null
    setWarnModalOpen(false)
    setSecondsUntilLeave(0)
  }, [])

  useEffect(() => {
    if (!enabled) {
      warnActiveRef.current = false
      warnStartedAtRef.current = null
      setWarnModalOpen(false)
      setSecondsUntilLeave(0)
      return
    }
    lastActivityRef.current = Date.now()

    const bump = () => {
      lastActivityRef.current = Date.now()
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        bump()
      }
    }

    const throttledMove = throttle(bump, 90_000)

    window.addEventListener('keydown', bump)
    window.addEventListener('mousedown', bump)
    window.addEventListener('touchstart', bump, { passive: true })
    window.addEventListener('wheel', bump, { passive: true })
    const scrollOpts: AddEventListenerOptions = { passive: true, capture: true }
    window.addEventListener('scroll', bump, scrollOpts)
    window.addEventListener('mousemove', throttledMove)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.removeEventListener('keydown', bump)
      window.removeEventListener('mousedown', bump)
      window.removeEventListener('touchstart', bump)
      window.removeEventListener('wheel', bump)
      window.removeEventListener('scroll', bump, scrollOpts)
      window.removeEventListener('mousemove', throttledMove)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return

    const tick = () => {
      const cf = getCallFrame()
      if (!cf) return

      const now = Date.now()
      const idleMs = now - lastActivityRef.current

      if (!warnActiveRef.current && idleMs >= warnAfterMs) {
        warnActiveRef.current = true
        warnStartedAtRef.current = now
        setWarnModalOpen(true)
      }

      if (warnActiveRef.current && warnStartedAtRef.current !== null) {
        const elapsedWarn = now - warnStartedAtRef.current
        const remain = Math.max(0, Math.ceil((graceAfterWarnMs - elapsedWarn) / 1000))
        setSecondsUntilLeave(remain)
        if (elapsedWarn >= graceAfterWarnMs) {
          warnActiveRef.current = false
          warnStartedAtRef.current = null
          setWarnModalOpen(false)
          setSecondsUntilLeave(0)
          void cf.leave().catch(() => {})
        }
      }
    }

    const id = window.setInterval(tick, 1000)
    tick()
    return () => window.clearInterval(id)
  }, [enabled, getCallFrame, warnAfterMs, graceAfterWarnMs])

  return {
    warnModalOpen,
    secondsUntilLeave,
    acknowledgeIdle: acknowledge,
  }
}
